import {
  Box,
  Button,
  Divider,
  Grid,
  LinearProgress,
  Typography,
} from "@mui/material";
import { Game, Producer, neededPsForSb } from "../../types/game";
import { calculateMoleculeProgress, getCost } from "../../lib/calculator";
import { useEffect, useState } from "react";
import {
  BuyAllButton,
  BuySettingButton,
  StrengtheningBoostButton,
  UnlockNextProducerButton,
  ProducerStrengtheningButton,
  PsButton,
} from "../GameButtons";
import React from "react";
import ProducerCard from "../basic/ProducerCard";
import executeProducerStrengthening, {
  executeStrengtheningBoost,
} from "../../lib/resets";
import { TabHeader } from "./TabHeader";
import ProducerStrengtheningDialog from "../dialogs/ProducerStrengtheningDialog";
import { loadObjectFromLocalStorage } from "../../save/localSave";
import StrengtheningBoostDialog from "../dialogs/StrengtheningBoostDialog";
import { GameContext } from "../../provider/GameProvider";
import { DialogContext } from "../../provider/DialogProvider";
import Decimal from "break_infinity.js";

export default function ProducerTab({}: {}) {
  const { setBuySetting, setTimeProgressArray, game, setGame } =
    React.useContext(GameContext);
  const [psDialogOpen, setPsDialogOpen] = React.useState(false);
  const [sbDialogOpen, setSbDialogOpen] = React.useState(false);
  const hidePsDialog =
    loadObjectFromLocalStorage("hideProducerStrengtheningDialog") ?? false;
  const hideSbDialog =
    loadObjectFromLocalStorage("hideStrengtheningBoostDialog") ?? false;

  const requiredProducerUnits =
    game.producerStrengthening.upgradeLevel + 6 - game.producers.length || 1;

  const lastProducer = game.producers[game.producers.length - 1];

  const toMultiplyProducerId =
    (game.producerStrengthening.upgradeLevel + 1) %
      (game.producers.length - 1) || game.producers.length - 1;

  const toMultiplyProducer = game.producers[toMultiplyProducerId];

  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    const handleResize = () => {
      setZoom(window.innerWidth <= 600 ? 0.5 : 1);
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Call the function initially to set the zoom based on the initial window size

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  return (
    <div
      style={{
        transform: `scale(${zoom})`,
      }}
    >
      <Grid container spacing={2}>
        <TabHeader />
        <Grid
          item
          xs={4}
          style={{
            justifyContent: "center",
            display: "flex",
            alignItems: "center",
          }}
        >
          <div style={{ display: "block" }}>
            <div style={{ display: "flex", gap: 12, paddingBottom: 8 }}>
              <BuySettingButton onClick={setBuySetting} />
              <BuyAllButton />
            </div>
            <div
              style={{ color: "white", fontSize: 10, alignContent: "center" }}
            >
              Buy 10 to boost Producer
            </div>
          </div>
        </Grid>
        <Grid item xs={4}>
          <Typography
            variant="h5"
            style={{
              color: "lightblue",
              textAlign: "center",
              paddingBottom: 32,
            }}
          >
            Producers
          </Typography>
        </Grid>
        <Grid item xs={4} />
        {game.producers.map((producer, index) => {
          if (index === 0) return null;
          if (!showUpgrade(producer, game.producers[0].value)) return null;
          return <ProducerGridItem index={index} key={index} />;
        })}
        <Grid item xs={12} style={{ paddingTop: 48, paddingBottom: 24 }}>
          <Divider
            style={{
              height: "1px",
              width: "100%",
            }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          xl={4}
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                color: "lightblue",
                fontWeight: "bold",
                paddingBottom: 16,
                textAlign: "center",
              }}
            >
              Producer Strengthening ({game.producerStrengthening.upgradeLevel})
            </div>
            <PsButton setPsDialogOpen={setPsDialogOpen} />
          </div>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          xl={4}
          style={{
            display: "flex",
          }}
        >
          {(game.producerStrengthening.upgradeLevel >= 4 ||
            game.strengtheningBoost.alreadySeen) && (
            <div>
              <div
                style={{
                  color: "lightblue",
                  fontWeight: "bold",
                  paddingBottom: 16,
                  textAlign: "center",
                }}
              >
                Strengthening Boost ({game.strengtheningBoost.upgradeLevel})
              </div>
              <StrengtheningBoostButton
                onClick={() => {
                  if (
                    game.producerStrengthening.upgradeLevel <
                    neededPsForSb + game.strengtheningBoost.upgradeLevel
                  )
                    return;
                  if (!hideSbDialog) {
                    setSbDialogOpen(true);
                  } else {
                    executeStrengtheningBoost(
                      game,
                      setGame,
                      setTimeProgressArray
                    );
                  }
                }}
              />
            </div>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            justifyContent: "center",
            paddingTop: 32,
          }}
        >
          <div
            style={{
              width: "80%",
              position: "relative",
              textAlign: "center",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                color: "lightblue",
                fontWeight: "bold",
                paddingBottom: 16,
                width: "100%",
              }}
            >
              Molecule Progress
            </div>
            <MoleculeProgressBar particles={game.producers[0].value} />
          </div>
        </Grid>
      </Grid>
      <ProducerStrengtheningDialog
        open={psDialogOpen}
        onClose={() => setPsDialogOpen(false)}
        onReset={() => {
          executeProducerStrengthening({
            game,
            setGame,
            setProgressTimes: setTimeProgressArray,
          });
        }}
      />
      <StrengtheningBoostDialog
        open={sbDialogOpen}
        onClose={() => setSbDialogOpen(false)}
        onReset={() => {
          executeStrengtheningBoost(game, setGame, setTimeProgressArray);
        }}
      />
    </div>
  );
}

function ProducerGridItem(props: { index: number }) {
  const { game, setGame } = React.useContext(GameContext);
  const producer = game.producers[props.index];
  useEffect(() => {
    if (!producer.alreadySeen) {
      setGame((prev) => ({
        ...prev,
        producers: prev.producers.map((p: Producer, index: number) => {
          if (index === props.index) {
            return {
              ...p,
              alreadySeen: true,
            };
          }
          return p;
        }),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Grid item xs={12} sm={12} md={6} xl={4} key={props.index}>
      <div
        style={{ color: "white", justifyContent: "center", display: "flex" }}
      >
        <ProducerCard index={props.index} />
      </div>
    </Grid>
  );
}

function showUpgrade(producer: Producer, particles: Decimal): boolean {
  const cost = getCost(producer);
  return (
    (particles.divide(cost).greaterThanOrEqualTo(0.1) ||
      producer.value.gt(0) ||
      !!producer.alreadySeen) &&
    producer.unlocked
  );
}

function MoleculeProgressBar(props: { particles: Decimal }) {
  const progress = calculateMoleculeProgress(props.particles, 1e50);
  const { setGame } = React.useContext(GameContext);
  useEffect(() => {
    if (progress >= 100) {
      setGame((prev) => ({
        ...prev,
        molecule: {
          ...prev.molecule,
          unlocked: true,
        },
      }));
    }
  }, [progress, setGame]);

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <Box sx={{ width: "100%" }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          style={{
            height: 20,
            borderRadius: 10,
            backgroundColor: "rgba(230, 255, 230, 0.7",
            width: "100%",
          }}
          sx={{
            "& .MuiLinearProgress-bar": {
              backgroundColor: "green",
            },
          }}
        />
      </Box>
      <div
        style={{
          color: "white",
          fontSize: 16,
          paddingLeft: 8,
        }}
      >
        {progress.toFixed(2)}%
      </div>
    </div>
  );
}
